import React, { useState, useRef } from "react";
import ReactDOM from 'react-dom';
import { IoClose } from "react-icons/io5";
import { useEffect } from "react";
import { FiLoader, FiDownload, FiEdit2, FiEye } from "react-icons/fi";
import { renderAsync } from 'docx-preview';
import mammoth from 'mammoth';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const modalRoot = document.getElementById('modal-root');

const Modal = ({ isOpen, onClose, title, children, headerActions, size = '2xl' }) => {
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isOpen]);

    if (!isOpen) {
        return null;
    }

    // Use ReactDOM.createPortal to render the modal outside of the current component hierarchy
    return ReactDOM.createPortal(
        // These classes are now applied to the viewport, guaranteeing centering
        <div
            className="fixed inset-0 backdrop-blur-sm bg-black/70 z-50 flex justify-center items-center p-4 transition-opacity"
            onClick={onClose}
        >
            <div
                className={`bg-zinc-900 rounded-xl shadow-2xl w-[95vw] h-[90vh] max-w-7xl flex flex-col border border-zinc-700
                     relative max-h-[90vh] max-w-${size}`}
                onClick={(e) => e.stopPropagation()}
            >
                {/* --- CHANGES ARE HERE --- */}
                <div className="flex justify-between items-center p-4 md:p-6 border-b border-slate-700">
                    <h3 className="text-xl font-semibold text-white">{title}</h3>

                    {/* Container for all buttons on the right */}
                    <div className="flex items-center gap-4">
                        {/* 1. Render the custom action buttons passed via props */}
                        {headerActions}

                        {/* 2. The original close button */}
                        <button
                            onClick={onClose}
                            className="p-1 rounded-full text-gray-400 hover:bg-slate-700 hover:text-white transition-colors"
                        >
                            <IoClose size={24} />
                        </button>
                    </div>
                </div>

                {/* Modal Body */}
                <div className="p-4 md:p-6 overflow-y-auto">
                    {children}
                </div>
            </div>
        </div>,
        modalRoot // The target DOM node for the portal
    );
};

//Docs preview
const DocxPreviewModal = ({ file, isOpen, onClose, filename }) => {
    const previewRef = useRef(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isEditing, setIsEditing] = useState(false);
    const [htmlContent, setHtmlContent] = useState("");
    const [hasChanges, setHasChanges] = useState(false);

    useEffect(() => {
        if (isOpen && file && previewRef.current && !isEditing) {
            setIsLoading(true);
            const reader = new FileReader();
            reader.onload = function (event) {
                const arrayBuffer = event.target.result;
                renderAsync(arrayBuffer, previewRef.current, null, { inWrapper: true, ignoreHeight: false })
                    .then(() => setIsLoading(false))
                    .catch(err => {
                        console.error("Preview render error:", err);
                        setIsLoading(false);
                    });
            };
            reader.readAsArrayBuffer(file);
        }
    }, [isOpen, file, isEditing]);

    const handleEditToggle = async () => {
        if (!isEditing) {
            try {
                setIsLoading(true);
                const arrayBuffer = await file.arrayBuffer();
                const result = await mammoth.convertToHtml({ arrayBuffer: arrayBuffer });
                setHtmlContent(result.value);
                setIsEditing(true);
            } catch (err) {
                console.error("Mammoth conversion error:", err);
                alert("Failed to extract document text for editing.");
            } finally {
                setIsLoading(false);
            }
        } else {
            setIsEditing(false);
        }
    };

    const handleHtmlChange = (val) => {
        setHtmlContent(val);
        setHasChanges(true);
    };

    const handleInternalDownload = () => {
        if (!file) return;

        if (isEditing && hasChanges) {
            const wordHtml = `
               <html xmlns:o='urn:schemas-microsoft-com:office:office' xmlns:w='urn:schemas-microsoft-com:office:word' xmlns='http://www.w3.org/TR/REC-html40'>
               <head><meta charset='utf-8'><title>${file?.name || 'Document'}</title></head>
               <body>${htmlContent}</body>
               </html>
             `;
            const blob = new Blob(['\ufeff', wordHtml], { type: 'application/msword' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', (file?.name ? file.name.replace('.docx', '') : 'edited_doc') + '.doc');
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
            return;
        }

        const url = URL.createObjectURL(file);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', file.name || 'template.docx');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const modalActions = (
        <div className="flex items-center gap-2">
            <button
                onClick={handleEditToggle}
                className={`px-3 py-2 rounded-lg transition-colors flex items-center gap-2 text-sm ${isEditing ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'bg-zinc-800 hover:bg-zinc-700 text-zinc-200'}`}
                title="Toggle Edit Mode"
            >
                {isEditing ? <FiEye className="w-4 h-4" /> : <FiEdit2 className="w-4 h-4" />}
                <span className="hidden sm:inline">{isEditing ? 'View Mode' : 'Edit Doc'}</span>
            </button>
            <button
                onClick={handleInternalDownload}
                className="px-3 py-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-200 rounded-lg transition-colors flex items-center gap-2 text-sm"
                title="Download file"
            >
                <FiDownload className="w-4 h-4" />
                <span className="hidden sm:inline">{isEditing && hasChanges ? 'Save / Download' : 'Download'}</span>
            </button>
        </div>
    );

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={file?.name || "Document Preview"}
            size="4xl"
            headerActions={modalActions}
        >

            {isLoading && (
                <div className="flex justify-center items-center h-96">
                    <FiLoader className="animate-spin text-4xl text-[#5652CF]" />
                </div>
            )}

            {isEditing ? (
                <div className="bg-white text-black h-full rounded-lg overflow-hidden flex flex-col min-h-[500px]">
                    <ReactQuill
                        theme="snow"
                        value={htmlContent}
                        onChange={handleHtmlChange}
                        className="flex-1 overflow-y-auto"
                        modules={{
                            toolbar: [
                                [{ header: [1, 2, 3, 4, 5, 6, false] }],
                                [{ font: [] }],
                                [{ color: [] }, { background: [] }],
                                [{ align: [] }],
                                ['bold', 'italic', 'underline', 'strike', 'blockquote'],
                                [{ list: 'ordered' }, { list: 'bullet' }, { indent: '-1' }, { indent: '+1' }],
                                ['link', 'image', 'video'],
                                ['clean']
                            ]
                        }}
                    />
                </div>
            ) : (
                <div ref={previewRef} className={`docx-preview-container ${isLoading ? 'hidden' : ''}`}></div>
            )}

        </Modal>
    );
};

export default DocxPreviewModal;